<template>
  <v-container>
    <v-row v-if="isLoading">
      <v-col cols="12">
        <v-skeleton-loader type="card" />
      </v-col>
      <v-col cols="12">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>
    <v-fade-transition>
      <v-row v-if="!isLoading">
        <v-col cols="12">
          <v-card class="custom card-1 pa-4" flat>
            <v-card-title class="section-title"
              >Configuração de Marca</v-card-title
            >
            <v-card-text>
              <v-form ref="formRef">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.subdomain"
                      :rules="subdomainRules"
                      name="subdomain"
                      label="Subdomínio *"
                      :suffix="'.' + baseDomain"
                      :disabled="submitting"
                      hide-details="auto"
                      @update:model-value="normalizeSubdomain"
                    />
                    <a
                      v-if="form.subdomain"
                      :href="subdomainUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="subdomain-link"
                    >
                      {{ subdomainUrl }}
                      <v-icon size="small" class="ml-1" :icon="mdiOpenInNew" />
                    </a>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.display_name"
                      :rules="[validationRules.maxLength(255)]"
                      name="display_name"
                      label="Nome de Exibição"
                      :disabled="submitting"
                      hide-details="auto"
                    />
                  </v-col>
                </v-row>
                <div class="d-flex align-center justify-end mt-4 ga-4">
                  <v-switch
                    v-model="form.is_active"
                    label="Ativo"
                    color="primary"
                    :disabled="submitting"
                    hide-details
                    density="compact"
                  />
                  <v-btn
                    color="primary"
                    :loading="submitting"
                    :disabled="submitting"
                    @click="saveBranding"
                  >
                    Salvar
                  </v-btn>
                </div>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card class="custom card-1 pa-4" flat>
            <v-card-title>Logos</v-card-title>
            <v-card-text>
              <v-alert v-if="!branding.subdomain" type="info" variant="tonal">
                Salve as configurações de marca antes de enviar os logotipos.
              </v-alert>
              <v-row v-else>
                <v-col cols="12" md="6">
                  <v-card variant="outlined" class="logo-card pa-4">
                    <v-card-subtitle class="mb-2">
                      Logo para fundo claro
                      <v-tooltip location="bottom">
                        <template v-slot:activator="{ props }">
                          <v-icon
                            size="small"
                            v-bind="props"
                            :icon="mdiHelpCircleOutline"
                          />
                        </template>
                        <span>Logo escura exibida sobre fundos claros</span>
                      </v-tooltip>
                    </v-card-subtitle>
                    <div
                      class="logo-preview light-bg"
                      v-if="branding.logo_light_url"
                    >
                      <img
                        :src="branding.logo_light_url"
                        alt="Logo para fundo claro"
                      />
                    </div>
                    <div class="logo-placeholder light-bg" v-else>
                      <v-icon
                        size="large"
                        color="grey-lighten-1"
                        :icon="mdiImageOutline"
                      />
                    </div>
                    <div class="logo-actions mt-3">
                      <v-file-input
                        v-model="logoLightFile"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        label="Selecionar arquivo"
                        :prepend-icon="mdiCamera"
                        :disabled="uploadingLight || submitting"
                        :loading="uploadingLight"
                        show-size
                        density="compact"
                        hide-details
                        @update:model-value="onLogoSelect('light')"
                      />
                      <v-btn
                        v-if="branding.logo_light_url"
                        variant="text"
                        color="error"
                        size="small"
                        class="mt-2"
                        :disabled="uploadingLight"
                        @click="confirmDeleteLogo('light')"
                      >
                        <v-icon start size="small" :icon="mdiDelete" />
                        Remover
                      </v-btn>
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card variant="outlined" class="logo-card pa-4">
                    <v-card-subtitle class="mb-2">
                      Logo para fundo escuro
                      <v-tooltip location="bottom">
                        <template v-slot:activator="{ props }">
                          <v-icon
                            size="small"
                            v-bind="props"
                            :icon="mdiHelpCircleOutline"
                          />
                        </template>
                        <span>Logo clara exibida sobre fundos escuros</span>
                      </v-tooltip>
                    </v-card-subtitle>
                    <div
                      class="logo-preview dark-bg"
                      v-if="branding.logo_dark_url"
                    >
                      <img
                        :src="branding.logo_dark_url"
                        alt="Logo para fundo escuro"
                      />
                    </div>
                    <div class="logo-placeholder dark-bg" v-else>
                      <v-icon
                        size="large"
                        color="grey"
                        :icon="mdiImageOutline"
                      />
                    </div>
                    <div class="logo-actions mt-3">
                      <v-file-input
                        v-model="logoDarkFile"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        label="Selecionar arquivo"
                        :prepend-icon="mdiCamera"
                        :disabled="uploadingDark || submitting"
                        :loading="uploadingDark"
                        show-size
                        density="compact"
                        hide-details
                        @update:model-value="onLogoSelect('dark')"
                      />
                      <v-btn
                        v-if="branding.logo_dark_url"
                        variant="text"
                        color="error"
                        size="small"
                        class="mt-2"
                        :disabled="uploadingDark"
                        @click="confirmDeleteLogo('dark')"
                      >
                        <v-icon start size="small" :icon="mdiDelete" />
                        Remover
                      </v-btn>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-fade-transition>

    <v-dialog v-model="deleteDialog" max-width="400" persistent>
      <v-card>
        <v-card-title>Remover logo</v-card-title>
        <v-card-text>
          Tem certeza que deseja remover este logo? Esta ação não pode ser
          desfeita.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="uploadingLight || uploadingDark"
            @click="executeDeleteLogo"
          >
            Remover
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useRoute } from "vue-router";
import {
  mdiOpenInNew,
  mdiHelpCircleOutline,
  mdiImageOutline,
  mdiCamera,
  mdiDelete,
} from "@mdi/js";
import { useSnackbar } from "@/plugins/snackbar";
import { validationRules } from "@/utils/validation";
import {
  useBrandingQuery,
  useUpdateBrandingMutation,
  useUploadLogoMutation,
  useDeleteLogoMutation,
} from "@/queries/modules/branding";

const route = useRoute();
const $snackbar = useSnackbar();

const tenantId = computed(() => {
  return route.params.tenant_id ? Number(route.params.tenant_id) : null;
});

const formRef = ref(null);
const submitting = ref(false);
const uploadingLight = ref(false);
const uploadingDark = ref(false);
const logoLightFile = ref(null);
const logoDarkFile = ref(null);
const deleteDialog = ref(false);
const deleteVariant = ref(null);

const branding = ref({
  subdomain: null,
  display_name: null,
  logo_light_url: null,
  logo_dark_url: null,
  primary_color: null,
  is_default: false,
});

const form = ref({
  subdomain: "",
  display_name: "Veraciti",
  primary_color: "#1976D2",
  is_active: true,
});

const subdomainRules = [
  validationRules.required,
  validationRules.minLength(1),
  validationRules.maxLength(63),
  (v) => /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(v) || "Subdomínio inválido",
];

const baseDomain = computed(() => {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  return parts.slice(1).join(".");
});

const subdomainUrl = computed(() => {
  const protocol = window.location.protocol;
  return `${protocol}//${form.value.subdomain}.${baseDomain.value}`;
});

// TanStack Query
const { data: brandingData, isLoading } = useBrandingQuery(tenantId);
const updateBrandingMutation = useUpdateBrandingMutation(tenantId);
const uploadLogoMutation = useUploadLogoMutation(tenantId);
const deleteLogoMutation = useDeleteLogoMutation(tenantId);

watch(
  brandingData,
  (data) => {
    if (data) {
      branding.value = data;
      form.value.subdomain = data.subdomain || "";
      form.value.display_name = data.display_name || "Veraciti";
      form.value.primary_color = data.primary_color || "#1976D2";
      form.value.is_active = data.is_active !== false;
    }
  },
  { immediate: true },
);

const normalizeSubdomain = () => {
  form.value.subdomain = form.value.subdomain
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "");
};

const getErrorMessage = (e, fallback) => {
  if (e.response && e.response.data && e.response.data.detail) {
    return e.response.data.detail;
  }
  return e.message || fallback;
};

const saveBranding = async () => {
  const { valid } = await formRef.value.validate();
  if (!valid) return;

  submitting.value = true;
  try {
    const payload = {
      subdomain: form.value.subdomain,
      display_name: form.value.display_name || null,
      primary_color: form.value.primary_color || null,
      is_active: form.value.is_active,
    };
    const data = await updateBrandingMutation.mutateAsync(payload);
    branding.value = data;
    $snackbar.success("Configurações salvas com sucesso");
  } catch (e) {
    $snackbar.error(getErrorMessage(e, "Erro ao salvar configurações"));
  } finally {
    submitting.value = false;
  }
};

const onLogoSelect = async (variant) => {
  const file = variant === "light" ? logoLightFile.value : logoDarkFile.value;
  if (!file) return;

  if (file.size > 2 * 1024 * 1024) {
    $snackbar.error("O arquivo deve ter no máximo 2MB");
    if (variant === "light") {
      logoLightFile.value = null;
    } else {
      logoDarkFile.value = null;
    }
    return;
  }

  await uploadLogo(variant, file);
};

const uploadLogo = async (variant, file) => {
  if (variant === "light") {
    uploadingLight.value = true;
  } else {
    uploadingDark.value = true;
  }

  try {
    const data = await uploadLogoMutation.mutateAsync({ variant, file });
    branding.value = data;
    $snackbar.success("Logo enviado com sucesso");
  } catch (e) {
    $snackbar.error(getErrorMessage(e, "Erro ao enviar logo"));
  } finally {
    if (variant === "light") {
      uploadingLight.value = false;
      logoLightFile.value = null;
    } else {
      uploadingDark.value = false;
      logoDarkFile.value = null;
    }
  }
};

const confirmDeleteLogo = (variant) => {
  deleteVariant.value = variant;
  deleteDialog.value = true;
};

const executeDeleteLogo = async () => {
  const variant = deleteVariant.value;
  deleteDialog.value = false;

  if (variant === "light") {
    uploadingLight.value = true;
  } else {
    uploadingDark.value = true;
  }

  try {
    const data = await deleteLogoMutation.mutateAsync(variant);
    branding.value = data;
    $snackbar.success("Logo removido com sucesso");
  } catch (e) {
    $snackbar.error(getErrorMessage(e, "Erro ao remover logo"));
  } finally {
    if (variant === "light") {
      uploadingLight.value = false;
    } else {
      uploadingDark.value = false;
    }
  }
};
</script>

<style scoped>
.section-title {
  font-size: 1.15rem;
  font-weight: 600;
}

.logo-card {
  height: 100%;
}

.logo-preview,
.logo-placeholder {
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.logo-preview img {
  max-height: 110px;
  max-width: 100%;
  object-fit: contain;
}

.dark-bg {
  background-color: #2d2d2d;
}

.light-bg {
  background-color: #f8f8f8;
}

.subdomain-link {
  display: inline-flex;
  align-items: center;
  font-size: 0.875rem;
  text-decoration: none;
  color: rgb(var(--v-theme-primary));
  margin-top: -4px;
}

.subdomain-link:hover {
  text-decoration: underline;
}
</style>
